/* ============================================================
   <barrier-drawer> — modular info drawer
   Extends ModalDialog (assets/global.js) for focus-trap,
   escape-to-close, click-outside-to-close, body scroll-lock,
   and media pausing.
   ============================================================ */

if (!customElements.get('barrier-drawer')) {
  class BarrierDrawer extends ModalDialog {
    constructor() {
      super();
    }

    connectedCallback() {
      if (this.moved) return;
      this.moved = true;
      document.body.appendChild(this);
    }

    show(opener) {
      // Browsers that support scrollbar-gutter: stable already reserve the
      // scrollbar gutter — no JS compensation needed. For older browsers,
      // measure the scrollbar and pad the body so adding overflow-hidden
      // doesn't reflow the page (no white flash where the scrollbar was).
      const supportsGutter = window.CSS && CSS.supports && CSS.supports('scrollbar-gutter', 'stable');
      if (!supportsGutter) {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
        if (scrollbarWidth > 0) {
          document.documentElement.style.setProperty('--barrier-drawer-scrollbar', scrollbarWidth + 'px');
          document.body.classList.add('barrier-drawer--scrollbar-pad');
        }
      }

      try {
        super.show(opener);
      } catch (err) {
        console.warn('[barrier-drawer] super.show threw, continuing:', err);
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
      }
      // Force layout/style flush so the initial transform+opacity are
      // committed before the .is-open class triggers the transition.
      // Without this, the browser collapses the two style changes and
      // we see a one-frame flash of the panel at its final position.
      void this.offsetWidth;
      requestAnimationFrame(() => {
        this.classList.add('is-open');
      });
    }

    hide() {
      const releaseScrollbarPad = () => {
        document.body.classList.remove('barrier-drawer--scrollbar-pad');
        document.documentElement.style.removeProperty('--barrier-drawer-scrollbar');
      };

      if (!this.classList.contains('is-open')) {
        super.hide();
        releaseScrollbarPad();
        return;
      }

      const panel = this.querySelector('.barrier-drawer__panel');
      let closed = false;
      const finalize = () => {
        if (closed) return;
        closed = true;
        super.hide();
        releaseScrollbarPad();
      };

      this.classList.remove('is-open');

      if (panel) {
        const onEnd = (event) => {
          if (event.target !== panel || event.propertyName !== 'transform') return;
          panel.removeEventListener('transitionend', onEnd);
          finalize();
        };
        panel.addEventListener('transitionend', onEnd);
        setTimeout(finalize, 400);
      } else {
        finalize();
      }
    }
  }

  customElements.define('barrier-drawer', BarrierDrawer);
}

/* ============================================================
   Global click delegation for the magic URL convention.
   Any <a href="#drawer:my-id"> (or trailing in any URL)
   or <button data-drawer-target="my-id"> opens the matching
   drawer if it exists on the current page.
   ============================================================ */

if (!window.__barrierDrawerDelegateInstalled) {
  window.__barrierDrawerDelegateInstalled = true;

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('a[href*="#drawer:"], button[data-drawer-target], a[data-drawer-target]');
    if (!trigger) return;

    let id = trigger.dataset && trigger.dataset.drawerTarget;
    if (!id) {
      const href = trigger.getAttribute('href') || '';
      const match = href.match(/#drawer:([^?#&\s]+)/);
      if (match) id = decodeURIComponent(match[1]);
    }
    if (!id) return;

    const drawer = document.getElementById('Drawer-' + id);
    if (!drawer) {
      console.warn('[barrier-drawer] No drawer found with id "' + id + '"');
      return;
    }

    event.preventDefault();

    document.querySelectorAll('barrier-drawer[open]').forEach((other) => {
      if (other !== drawer) other.hide();
    });

    drawer.show(trigger);
  });
}
